class StateManager {
  constructor() {
    console.log('StateManager: Initializing...');
    this.currentView = null;
    this.previousView = null; // Track previous view for world map restoration
    this.isTransitioning = false;
    this.TRANSITION_DURATION = 500;
    this.TRANSITION_TIMEOUT = 2000; // 2 second timeout for transitions
    
    // Add mutex for main menu operations
    this.isMainMenuTransitioning = false;
    
    // Add timestamp tracking for debugging
    this.lastMainMenuCall = 0;
    
    // Store timeout references to prevent race conditions
    this.pendingTimeouts = new Map();
    
    // Cache DOM elements with more detailed logging
    this.elements = {
      titleScreen: null, // Will be initialized dynamically
      productSelector: null, // Will be initialized dynamically
      header: document.querySelector('.hound-header'),
      rpgShops: [], // Will be initialized dynamically
      regularHeader: null, // Will be initialized dynamically
      guideSection: null // Will be initialized dynamically
    };

    // Initialize elements that might be loaded dynamically
    this.initializeDynamicElements();

    // Log detailed information about found elements
    console.log('StateManager: Element Details:', {
      titleScreen: {
        found: !!this.elements.titleScreen,
        element: this.elements.titleScreen
      },
      productSelector: {
        found: !!this.elements.productSelector,
        element: this.elements.productSelector,
        id: this.elements.productSelector?.id,
        classList: this.elements.productSelector?.classList,
        style: this.elements.productSelector?.style
      },
      header: {
        found: !!this.elements.header,
        element: this.elements.header
      },
      regularHeader: {
        found: !!this.elements.regularHeader,
        element: this.elements.regularHeader
      },
      rpgShops: {
        count: this.elements.rpgShops.length,
        elements: this.elements.rpgShops.map(shop => ({
          id: shop.id,
          classList: shop.classList
        }))
      },
      guideSection: {
        found: !!this.elements.guideSection,
        element: this.elements.guideSection
      }
    });

    // Ensure elements have transition properties
    this.initializeTransitions();

    console.log('StateManager: Found elements:', {
      titleScreen: !!this.elements.titleScreen,
      productSelector: !!this.elements.productSelector,
      header: !!this.elements.header,
      regularHeader: !!this.elements.regularHeader,
      rpgShops: this.elements.rpgShops.length,
      guideSection: !!this.elements.guideSection
    });

    // Initialize event listeners
    this.initializeEventListeners();
    console.log('StateManager: Initialization complete');
  }

  initializeDynamicElements() {
    // Try to find title screen with different selectors
    const titleScreenSelectors = [
      '.title-screen-container',
      '[id^="shopify-section-"][id*="__title_screen"]'
    ];

    for (const selector of titleScreenSelectors) {
      this.elements.titleScreen = document.querySelector(selector);
      if (this.elements.titleScreen) {
        console.log('StateManager: Found title screen with selector:', selector);
        break;
      }
    }

    // Try to find product selector with different selectors
    const productSelectorSelectors = [
      '[id^="shopify-section-"][id*="__product_selector_buttons"]',
      '.product-selector-buttons'
    ];

    for (const selector of productSelectorSelectors) {
      this.elements.productSelector = document.querySelector(selector);
      if (this.elements.productSelector) {
        console.log('StateManager: Found product selector with selector:', selector);
        break;
      }
    }

    // Try to find regular header with different selectors
    const regularHeaderSelectors = [
      '.header',
      'header-drawer',
      '.shopify-section-group-header-group'
    ];

    for (const selector of regularHeaderSelectors) {
      this.elements.regularHeader = document.querySelector(selector);
      if (this.elements.regularHeader) {
        console.log('StateManager: Found regular header with selector:', selector);
        break;
      }
    }

    // Find guide section
    this.elements.guideSection = document.querySelector('.guide-section');
    if (this.elements.guideSection) {
      console.log('StateManager: Found guide section:', {
        element: this.elements.guideSection,
        classes: this.elements.guideSection.classList.toString(),
        style: this.elements.guideSection.style.cssText
      });
      // Remove any inline styles that might interfere
      this.elements.guideSection.style = '';
    } else {
      console.warn('StateManager: Guide section not found!');
    }

    // Initialize RPG shops
    this.updateRpgShops();

    // If elements still not found, wait for DOM changes
    if (!this.elements.titleScreen || !this.elements.productSelector || !this.elements.regularHeader) {
      console.log('StateManager: Setting up observer for missing elements');
      const observer = new MutationObserver((mutations) => {
        let foundAll = true;

        if (!this.elements.titleScreen) {
          for (const selector of titleScreenSelectors) {
            const element = document.querySelector(selector);
            if (element) {
              console.log('StateManager: Title screen found via observer');
              this.elements.titleScreen = element;
              break;
            }
          }
          foundAll = foundAll && !!this.elements.titleScreen;
        }

        if (!this.elements.productSelector) {
          for (const selector of productSelectorSelectors) {
            const element = document.querySelector(selector);
            if (element) {
              console.log('StateManager: Product selector found via observer');
              this.elements.productSelector = element;
              break;
            }
          }
          foundAll = foundAll && !!this.elements.productSelector;
        }

        if (!this.elements.regularHeader) {
          for (const selector of regularHeaderSelectors) {
            const element = document.querySelector(selector);
            if (element) {
              console.log('StateManager: Regular header found via observer');
              this.elements.regularHeader = element;
              break;
            }
          }
          foundAll = foundAll && !!this.elements.regularHeader;
        }

        // Update RPG shops on any DOM change
        this.updateRpgShops();

        if (foundAll) {
          console.log('StateManager: All elements found, disconnecting observer');
          observer.disconnect();
          // Re-initialize transitions for newly found elements
          this.initializeTransitions();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  updateRpgShops() {
    // Find all RPG shop sections
    const rpgShopSections = document.querySelectorAll('[id^="shopify-section-"][id*="__rpg_shop_"]');
    console.log('StateManager: Found RPG shops:', rpgShopSections.length);
    
    // Update the elements array with new shops
    this.elements.rpgShops = Array.from(rpgShopSections);
    
    // Initialize transitions for any new shops
    this.elements.rpgShops.forEach(shop => {
      if (!shop.style.transition) {
        shop.style.transition = `opacity ${this.TRANSITION_DURATION}ms ease`;
      }
    });
  }

  initializeTransitions() {
    // Add transition properties to elements
    if (this.elements.titleScreen) {
      this.elements.titleScreen.style.transition = `opacity ${this.TRANSITION_DURATION}ms ease`;
      this.elements.titleScreen.style.display = 'flex';
    }
    if (this.elements.productSelector) {
      this.elements.productSelector.style.transition = `opacity ${this.TRANSITION_DURATION}ms ease`;
      this.elements.productSelector.style.display = 'none';
    }
    if (this.elements.header) {
      this.elements.header.style.transition = `opacity ${this.TRANSITION_DURATION}ms ease`;
      this.elements.header.style.display = 'none';
    }
    this.elements.rpgShops.forEach(shop => {
      shop.style.transition = `opacity ${this.TRANSITION_DURATION}ms ease`;
      shop.style.display = 'none';
    });
  }

  initializeEventListeners() {
    console.log('StateManager: Setting up event listeners');
    // Listen for navigation events
    document.addEventListener('navigate', (e) => {
      console.log('StateManager: Received navigation event:', e.detail);
      this.navigateTo(e.detail.view);
    });

    // Listen for product selector changes
    document.addEventListener('productSelectorChange', (e) => {
      console.log('StateManager: Received product selector change:', e.detail);
      this.handleProductSelectorChange(e.detail);
    });
  }

  handleProductSelectorChange(detail) {
    console.log('StateManager: Handling product selector change');
    
    // Update RPG shops list
    this.updateRpgShops();
    
    // Find the active shop
    const activeShop = this.elements.rpgShops.find(shop => shop.style.display !== 'none' && shop.style.opacity === '1');
    if (!activeShop) {
      console.log('StateManager: No active shop found');
      return;
    }

    // Update height after a delay to ensure content is rendered
    setTimeout(() => {
      const activeContent = activeShop.querySelector('.activeContent');
      if (activeContent) {
        console.log('StateManager: Updating height for active content');
        if (typeof updateHeight === 'function') {
          updateHeight();
        } else {
          console.warn('StateManager: updateHeight function not found');
        }
      }
    }, 100);
  }

  async navigateTo(view) {
    if (this.currentView === view) {
      console.log('[NAV] Already on', view, '- navigation ignored.');
      return;
    }
    console.log('[NAV] Navigating to view:', view);
    if (this.isTransitioning) {
      console.log('[NAV] Already transitioning, ignoring navigation request');
      return;
    }
    if (this.currentView === 'main-menu' && view !== 'merchants-den' && view !== 'guide' && view !== 'world-map') {
      console.log('[NAV] On main menu, only allowing navigation to merchants-den, guide, or world-map');
      return;
    }
    this.previousView = this.currentView;
    const transitionMap = {
      'merchants-den:guide': 'merchants-den-to-guide',
      'guide:merchants-den': 'guide-to-merchants-den'
    };
    const transitionKey = `${this.currentView}:${view}`;
    const transitionType = transitionMap[transitionKey];
    this.isTransitioning = true;
    try {
      document.body.offsetHeight;
      
      if (transitionType && window.fixedVideoBackground && typeof window.fixedVideoBackground.playTransition === 'function') {
        console.log('[NAV] Playing transition video:', transitionType);
        
        // PRELOAD DESTINATION BACKGROUND VIDEOS EARLY
        // This ensures they are ready before the transition video ends
        if (view === 'guide') {
          console.log('[NAV] Early preloading guide background videos');
          const isMobile = window.matchMedia('(max-width: 767px)').matches;
          const guideVideoId = isMobile ? 'guide-bg-default-mobile' : 'guide-bg-default-desktop';
          const guideVideo = document.getElementById(guideVideoId);
          if (guideVideo) {
            // Start loading the destination video immediately but keep it hidden
            guideVideo.load();
            guideVideo.currentTime = 0;
            guideVideo.muted = true;
            guideVideo.playsInline = true;
            guideVideo.setAttribute('playsinline', '');
            guideVideo.setAttribute('webkit-playsinline', '');
            // Ensure the video remains hidden during preloading
            guideVideo.style.display = 'none';
            guideVideo.style.opacity = '0';
            guideVideo.style.zIndex = '-1';
            guideVideo.style.visibility = 'hidden';
          }
        } else if (view === 'merchants-den') {
          console.log('[NAV] Early preloading merchant background video');
          const merchantVideo = document.getElementById('merchants-den-bg-video');
          if (merchantVideo) {
            // Start loading the destination video immediately but keep it hidden
            merchantVideo.load();
            merchantVideo.currentTime = 0;
            merchantVideo.muted = true;
            merchantVideo.playsInline = true;
            merchantVideo.setAttribute('playsinline', '');
            merchantVideo.setAttribute('webkit-playsinline', '');
            // Ensure the video remains hidden during preloading
            merchantVideo.style.display = 'none';
            merchantVideo.style.opacity = '0';
            merchantVideo.style.zIndex = '-1';
            merchantVideo.style.visibility = 'hidden';
          }
        }
        
        // ENSURE ALL BACKGROUND VIDEOS ARE HIDDEN DURING TRANSITION
        // This prevents any background video from showing during the transition
        console.log('[NAV] Ensuring all background videos are hidden during transition');
        const allBackgroundVideos = [
          'merchants-den-bg-video',
          'guide-bg-default-desktop',
          'guide-bg-talking-desktop', 
          'guide-bg-default-mobile',
          'guide-bg-talking-mobile'
        ];
        allBackgroundVideos.forEach(videoId => {
          const video = document.getElementById(videoId);
          if (video) {
            video.style.display = 'none';
            video.style.opacity = '0';
            video.style.zIndex = '-1';
            video.style.visibility = 'hidden';
          }
        });
        
        // Hide all UI elements before transition video for clean background
        console.log('[NAV] Hiding all UI elements before transition video');
        
        // Hide guide section with fade out (don't wait for completion)
        if (this.elements.guideSection) {
          console.log('[NAV] Fading out guide section for transition');
          this.elements.guideSection.classList.remove('visible');
          this.elements.guideSection.style.opacity = '0';
          this.elements.guideSection.style.pointerEvents = 'none';
          // Don't wait for fade out - let it happen in background
        }
        
        // Hide product selector
        if (this.elements.productSelector) {
          console.log('[NAV] Hiding product selector for transition');
          this.elements.productSelector.style.opacity = '0';
          this.elements.productSelector.style.pointerEvents = 'none';
          setTimeout(() => {
            this.elements.productSelector.style.display = 'none';
          }, this.TRANSITION_DURATION);
        }
        
        // Hide all RPG shops
        console.log('[NAV] Hiding RPG shops for transition');
        this.elements.rpgShops.forEach((shop, index) => {
          if (shop.style.display !== 'none' && shop.style.opacity !== '0') {
            console.log(`[NAV] Hiding RPG shop ${index} for transition`);
            shop.style.opacity = '0';
            setTimeout(() => {
              shop.style.display = 'none';
            }, this.TRANSITION_DURATION);
          }
        });
        
        // Keep header visible during transition for seamless UI feel
        console.log('[NAV] Keeping header visible during transition');
        
        // Show destination background immediately when transition ends (before it starts playing)
        await new Promise(resolve => {
          const originalPlayTransition = window.fixedVideoBackground.playTransition;
          
          // Override the playTransition to show destination background immediately
          window.fixedVideoBackground.playTransition = function(type, onComplete) {
            const isMobile = window.matchMedia('(max-width: 767px)').matches;
            const id = 'transition-' + type + '-' + (isMobile ? 'mobile' : 'desktop');
            const vid = document.getElementById(id);
            
            if (!vid) {
              if (typeof onComplete === 'function') onComplete();
              return;
            }
            
            // Hide all transition videos first
            [
              'transition-merchants-den-to-guide-desktop',
              'transition-merchants-den-to-guide-mobile',
              'transition-guide-to-merchants-den-desktop',
              'transition-guide-to-merchants-den-mobile'
            ].forEach(function(vidId) {
              const v = document.getElementById(vidId);
              if (v) {
                v.pause();
                v.style.display = 'none';
              }
            });
            
            // Show and play the correct video
            vid.style.display = 'block';
            vid.currentTime = 0;
            vid.muted = true;
            vid.playsInline = true;
            vid.setAttribute('playsinline', '');
            vid.setAttribute('webkit-playsinline', '');
            
            // When transition video ends, immediately show destination background
            vid.onended = function() {
              console.log('[NAV] Transition video ended, showing destination background immediately');
              
              // First show destination background to avoid gap
              if (view === 'guide') {
                console.log('[NAV] Immediately showing guide background');
                                  if (window.toggleGuideBgVideo) {
                    window.toggleGuideBgVideo('idle');
                    // Hide merchant background immediately (no delay needed since destination is preloaded)
                    if (window.toggleMerchantsDenBgVideo) {
                      window.toggleMerchantsDenBgVideo(false);
                    }
                  }
                              } else if (view === 'merchants-den') {
                  console.log('[NAV] Immediately showing merchant background');
                  if (window.toggleMerchantsDenBgVideo) {
                    window.toggleMerchantsDenBgVideo(true);
                    // Hide guide backgrounds immediately (no delay needed since destination is preloaded)
                    if (window.toggleGuideBgVideo) {
                      window.toggleGuideBgVideo('none');
                    }
                  }
                }
              
              // Then hide the transition video
              vid.style.display = 'none';
              
              if (typeof onComplete === 'function') onComplete();
            };
            
            vid.onerror = function() {
              vid.style.display = 'none';
              if (typeof onComplete === 'function') onComplete();
            };
            
            vid.play().catch(function() {
              // If play fails, fallback after 2s
              setTimeout(function() {
                vid.style.display = 'none';
                if (typeof onComplete === 'function') onComplete();
              }, 2000);
            });
          };
          
          // Call the overridden function
          window.fixedVideoBackground.playTransition(transitionType, () => {
            // Restore original function
            window.fixedVideoBackground.playTransition = originalPlayTransition;
            resolve();
          });
        });
      } else {
        // No transition video
        // For world map overlay, DO NOT change the background; keep whatever was visible
        if (view === 'guide') {
          if (window.toggleGuideBgVideo) window.toggleGuideBgVideo('idle');
          if (window.toggleMerchantsDenBgVideo) window.toggleMerchantsDenBgVideo(false);
        } else if (view === 'merchants-den') {
          if (window.toggleMerchantsDenBgVideo) window.toggleMerchantsDenBgVideo(true);
          if (window.toggleGuideBgVideo) window.toggleGuideBgVideo('none');
        } else if (view === 'world-map') {
          // leave backgrounds as-is
        } else {
          if (window.toggleGuideBgVideo) window.toggleGuideBgVideo('none');
          if (window.toggleMerchantsDenBgVideo) window.toggleMerchantsDenBgVideo(false);
        }
      }
      
      await new Promise(resolve => requestAnimationFrame(resolve));
      switch (view) {
        case 'main-menu':
          await this.showMainMenu();
          break;
        case 'merchants-den':
          await this.showMerchantsDen();
          break;
        case 'guide':
          await this.showGuide();
          break;
        case 'quest':
          await this.showQuest();
          break;
        case 'world-map':
          await this._displayWorldMap();
          break;
      }
      this.currentView = view;
      const event = new CustomEvent('navigationComplete', { detail: { view: view } });
      document.dispatchEvent(event);
      console.log('[NAV] Navigation complete to', view);
    } catch (error) {
      console.error('[NAV] Error during navigation:', error);
      this.currentView = this.previousView;
    } finally {
      this.isTransitioning = false;
      console.log('[NAV] Transitioning flag cleared');
    }
  }

  async hideAllSections() {
    console.log('StateManager: Hiding all sections');
    const promises = [];

    // Hide title screen
    if (this.elements.titleScreen) {
      console.log('StateManager: Hiding title screen');
      this.elements.titleScreen.style.opacity = '0';
      this.elements.titleScreen.style.pointerEvents = 'none';
      promises.push(this.waitForTransition(this.elements.titleScreen).then(() => {
        this.elements.titleScreen.style.display = 'none';
      }));
    }

    // Hide product selector
    if (this.elements.productSelector) {
      console.log('StateManager: Hiding product selector');
      this.elements.productSelector.style.opacity = '0';
      promises.push(this.waitForTransition(this.elements.productSelector).then(() => {
        this.elements.productSelector.style.display = 'none';
      }));
    }

    // Hide RPG header (hound-header)
    if (this.elements.header) {
      console.log('StateManager: Hiding RPG header');
      this.elements.header.style.opacity = '0';
      this.elements.header.style.pointerEvents = 'none';
      promises.push(this.waitForTransition(this.elements.header).then(() => {
        this.elements.header.style.display = 'none';
      }));
    }

    // Hide guide section
    if (this.elements.guideSection) {
      console.log('StateManager: Hiding guide section');
      this.elements.guideSection.classList.remove('visible');
      promises.push(this.waitForTransition(this.elements.guideSection).then(() => {
        // No need to set display: none as it's handled by CSS
      }));
    }

    // Update RPG shops before hiding them
    this.updateRpgShops();

    // Hide all RPG shops
    console.log('StateManager: Hiding RPG shops');
    this.elements.rpgShops.forEach(shop => {
      shop.style.opacity = '0';
      promises.push(this.waitForTransition(shop).then(() => {
        shop.style.display = 'none';
      }));
    });

    if (window.toggleMerchantsDenBgVideo) window.toggleMerchantsDenBgVideo(false);

    await Promise.all(promises);
    console.log('StateManager: All sections hidden');
  }

  async showMainMenu() {
    // Mutex protection to prevent concurrent execution
    if (this.isMainMenuTransitioning) {
      this.logWithTimestamp('🚫 Main menu transition already in progress, skipping request', {
        isMainMenuTransitioning: this.isMainMenuTransitioning,
        currentView: this.currentView,
        titleScreenDisplay: this.elements.titleScreen?.style.display,
        titleScreenOpacity: this.elements.titleScreen?.style.opacity
      });
      return;
    }

    if (this.currentView === 'main-menu') {
      this.logWithTimestamp('🚫 Already on main menu, skipping transition', {
        currentView: this.currentView,
        titleScreenDisplay: this.elements.titleScreen?.style.display,
        titleScreenOpacity: this.elements.titleScreen?.style.opacity
      });
      return;
    }

    this.logWithTimestamp('🎯 Starting showMainMenu transition', {
      currentView: this.currentView,
      isMainMenuTransitioning: this.isMainMenuTransitioning,
      titleScreenDisplay: this.elements.titleScreen?.style.display,
      titleScreenOpacity: this.elements.titleScreen?.style.opacity,
      titleScreenPointerEvents: this.elements.titleScreen?.style.pointerEvents
    });
    
    this.isMainMenuTransitioning = true;
    this.logWithTimestamp('🔒 Main menu mutex locked');
    
    try {
      this.logWithTimestamp('📦 About to call hideUnnecessaryElements("main-menu")');
      await this.hideUnnecessaryElements('main-menu');
      this.logWithTimestamp('✅ hideUnnecessaryElements completed');
      
      this.logWithTimestamp('🎨 Setting up title screen display');
      if (this.elements.titleScreen) {
        this.logWithTimestamp('   Title screen element found, setting styles');
        this.logWithTimestamp('   Before setting styles', {
          display: this.elements.titleScreen.style.display,
          opacity: this.elements.titleScreen.style.opacity,
          pointerEvents: this.elements.titleScreen.style.pointerEvents
        });
        
        this.elements.titleScreen.style.display = 'flex';
        this.elements.titleScreen.style.opacity = '1';
        this.elements.titleScreen.style.pointerEvents = 'auto';
        
        this.logWithTimestamp('   After setting styles', {
          display: this.elements.titleScreen.style.display,
          opacity: this.elements.titleScreen.style.opacity,
          pointerEvents: this.elements.titleScreen.style.pointerEvents
        });
      } else {
        this.logWithTimestamp('⚠️ Title screen element not found!');
      }

      this.logWithTimestamp('👤 Updating header visibility (hiding headers)');
      this.updateHeaderVisibility(false);
      
      this.logWithTimestamp('🔄 Setting currentView to main-menu');
      this.currentView = 'main-menu';

      this.logWithTimestamp('🎯 showMainMenu transition completed successfully', {
        currentView: this.currentView,
        titleScreenDisplay: this.elements.titleScreen?.style.display,
        titleScreenOpacity: this.elements.titleScreen?.style.opacity,
        titleScreenPointerEvents: this.elements.titleScreen?.style.pointerEvents
      });

      // Background video logic moved to the end of the method
    } catch (error) {
      this.logWithTimestamp('❌ Error in showMainMenu', error);
      console.error('   Error stack:', error.stack);
    } finally {
      // Always clear the mutex, even if there was an error
      this.logWithTimestamp('🔓 Clearing main menu mutex');
      this.isMainMenuTransitioning = false;
    }

    // Ensure background is only set if main-menu is still the current view
    if (this.currentView === 'main-menu') {
      this.logWithTimestamp('[BG] Setting background for main menu');
      if (window.toggleGuideBgVideo) {
        this.logWithTimestamp('[BG] Hiding guide background (toggleGuideBgVideo none)');
        window.toggleGuideBgVideo('none');
      }
      if (window.toggleMerchantsDenBgVideo) {
        this.logWithTimestamp('[BG] Hiding merchants-den background (toggleMerchantsDenBgVideo false)');
        window.toggleMerchantsDenBgVideo(false);
      }
    }
  }

  async showMerchantsDen() {
    if (this.currentView === 'merchants-den') {
      console.log('Already in merchant\'s den, skipping transition');
      return;
    }

    console.log('Showing merchant\'s den');
    
    // PHASE 1: Prepare sections
    await this.hideUnnecessaryElements('merchants-den');
    
    // Background video is now handled in the transition logic
    // PHASE 2-3: Background video preparation and transition video hiding removed
    
    // PHASE 4: Show product selector with proper fade-in
    if (this.elements.productSelector) {
      console.log('StateManager: Showing product selector');
      
      // Reset any previous state and prepare for fade-in
      this.elements.productSelector.style.transition = 'none';
      this.elements.productSelector.style.display = 'flex';
      this.elements.productSelector.style.opacity = '0';
      this.elements.productSelector.style.pointerEvents = 'none';
      
      // Force a reflow to ensure the transition works
      this.elements.productSelector.offsetHeight;
      
      // Enable transitions and fade in
      this.elements.productSelector.style.transition = '';
      this.elements.productSelector.style.opacity = '1';
      this.elements.productSelector.style.pointerEvents = 'auto';
      
      // Wait for the transition to complete
      await this.waitForTransition(this.elements.productSelector);
    }

    // PHASE 5: Show headers
    this.updateHeaderVisibility(true);
    // Hide world map overlay if visible
    const mapOverlay = document.querySelector('[id^="shopify-section-"][id*="__map_section"] .map-section') || document.querySelector('.map-section');
    if (mapOverlay) {
      mapOverlay.classList.remove('visible');
      setTimeout(() => {
        mapOverlay.style.display = 'none';
        mapOverlay.style.pointerEvents = 'none';
      }, this.TRANSITION_DURATION);
    }
    this.currentView = 'merchants-den';
  }

  async showRpgShop(shopId) {
    console.log('StateManager: Showing RPG shop:', shopId);
    
    // Update RPG shops before showing
    this.updateRpgShops();
    
    // Find the specific shop
    const shop = this.elements.rpgShops.find(s => s.id === shopId);
    if (!shop) {
      console.error('StateManager: RPG shop not found:', shopId);
      return;
    }

    // Show the shop
    shop.style.display = 'block';
    await new Promise(resolve => setTimeout(resolve, 50));
    shop.style.opacity = '1';
    await this.waitForTransition(shop);

    // Update height after the shop is visible
    const activeContent = shop.querySelector('.activeContent');
    if (activeContent) {
      console.log('StateManager: Updating height for active content');
      // Find the updateHeight function in the shop's scope
      const updateHeight = shop.querySelector('script')?.textContent?.match(/function\s+updateHeight\(\)\s*{([^}]*)}/)?.[0];
      if (updateHeight) {
        // Create a new function from the matched code and execute it
        const updateHeightFunc = new Function(updateHeight);
        updateHeightFunc();
      } else {
        console.warn('StateManager: updateHeight function not found in shop scope');
      }
    }

    // Show both RPG header and regular header
    if (this.elements.header) {
      console.log('StateManager: Showing RPG header');
      this.elements.header.style.display = 'block';
      this.elements.header.style.opacity = '0';
      this.elements.header.style.pointerEvents = 'auto';
      // Force reflow
      this.elements.header.offsetHeight;
      this.elements.header.style.opacity = '1';
      await new Promise(resolve => setTimeout(resolve, 50));
      await this.waitForTransition(this.elements.header);
    }

    // Show regular header if it exists
    if (this.elements.regularHeader) {
      console.log('StateManager: Showing regular header');
      // Remove any hiding styles
      this.elements.regularHeader.style.opacity = '1';
      this.elements.regularHeader.style.transform = 'none';
      this.elements.regularHeader.style.pointerEvents = 'auto';
      this.elements.regularHeader.style.transition = 'none';
      this.elements.regularHeader.style.display = 'block';
    }
  }

  waitForTransition(element) {
    console.log('StateManager: Waiting for transition on element:', element);
    return new Promise((resolve) => {
      const onTransitionEnd = () => {
        console.log('StateManager: Transition ended for element:', element);
        element.removeEventListener('transitionend', onTransitionEnd);
        resolve();
      };

      // Add both transitionend and webkitTransitionEnd for cross-browser support
      element.addEventListener('transitionend', onTransitionEnd);
      element.addEventListener('webkitTransitionEnd', onTransitionEnd);

      // Fallback timeout in case transition events don't fire
      setTimeout(() => {
        console.log('StateManager: Transition fallback timeout for element:', element);
        element.removeEventListener('transitionend', onTransitionEnd);
        element.removeEventListener('webkitTransitionEnd', onTransitionEnd);
        resolve();
      }, this.TRANSITION_DURATION + 100); // Add 100ms buffer
    });
  }

  updateHeaderVisibility(showHeaders) {
    console.log('👤 updateHeaderVisibility called with showHeaders:', showHeaders);
    console.log('   Current state:', {
      currentView: this.currentView,
      isMainMenuTransitioning: this.isMainMenuTransitioning,
      headerExists: !!this.elements.header,
      regularHeaderExists: !!this.elements.regularHeader
    });
    
    if (this.elements.header) {
      if (showHeaders) {
        console.log('   Showing RPG header');
        this.elements.header.style.display = 'block';
        this.elements.header.style.opacity = '0';
        this.elements.header.style.pointerEvents = 'auto';
        // Force reflow
        this.elements.header.offsetHeight;
        this.elements.header.style.opacity = '1';
      } else {
        console.log('   Hiding RPG header');
        this.elements.header.style.opacity = '0';
        this.elements.header.style.pointerEvents = 'none';
        setTimeout(() => {
          console.log('   Setting RPG header display to none');
          this.elements.header.style.display = 'none';
        }, this.TRANSITION_DURATION);
      }
    } else {
      console.log('   RPG header element not found');
    }

    if (this.elements.regularHeader) {
      if (showHeaders) {
        console.log('   Showing regular header');
        this.elements.regularHeader.style.display = 'block';
        this.elements.regularHeader.style.opacity = '1';
        this.elements.regularHeader.style.pointerEvents = 'auto';
      } else {
        console.log('   Hiding regular header');
        this.elements.regularHeader.style.opacity = '0';
        setTimeout(() => {
          console.log('   Setting regular header display to none');
          this.elements.regularHeader.style.display = 'none';
        }, this.TRANSITION_DURATION);
      }
    } else {
      console.log('   Regular header element not found');
    }
    
    console.log('✅ updateHeaderVisibility completed');
  }

  async showGuide() {
    if (this.currentView === 'guide') {
      console.log('StateManager: Already in guide, skipping transition');
      return;
    }

    console.log('StateManager: Showing guide');
    
    // PHASE 1: Prepare guide section but keep it hidden
    await this.hideUnnecessaryElements('guide');
    
    // Always re-query for the guide section in case it was dynamically loaded
    this.elements.guideSection = document.querySelector('.guide-section');
    if (this.elements.guideSection) {
      console.log('StateManager: Preparing guide section');
      
      // Reset any previous state and prepare for fade-in
      this.elements.guideSection.style.transition = 'none';
      this.elements.guideSection.classList.remove('visible');
      this.elements.guideSection.style.opacity = '0';
      this.elements.guideSection.style.pointerEvents = 'none';
      
      // Force a reflow to ensure the hiding takes effect
      this.elements.guideSection.offsetHeight;
    } else {
      console.warn('StateManager: Guide section not found when trying to show it!');
    }
    
    // PHASE 2: Show headers
    this.updateHeaderVisibility(true);
    
    // Background video is now handled in the transition logic
    // PHASE 3-4: Background video preparation and transition video hiding removed
    
    // Hide world map overlay if visible
    const mapOverlay = document.querySelector('[id^="shopify-section-"][id*="__map_section"] .map-section') || document.querySelector('.map-section');
    if (mapOverlay) {
      mapOverlay.classList.remove('visible');
      setTimeout(() => {
        mapOverlay.style.display = 'none';
        mapOverlay.style.pointerEvents = 'none';
      }, this.TRANSITION_DURATION);
    }

    // Now show the guide section with proper fade-in
    if (this.elements.guideSection) {
      // Enable transitions and fade in
      this.elements.guideSection.style.transition = '';
      this.elements.guideSection.classList.add('visible');
      this.elements.guideSection.style.opacity = '1';
      this.elements.guideSection.style.pointerEvents = 'auto';
      console.log('StateManager: Guide section classes after adding visible:', this.elements.guideSection.classList.toString());
    }
    
    // Update current view after everything is complete
    this.currentView = 'guide';
  }

  async showQuest() {
    if (this.currentView === 'quest') {
      console.log('Already in quest, skipping transition');
      return;
    }

    console.log('Showing quest');
    await this.hideUnnecessaryElements('quest');
    
    // Hide world map overlay if visible
    const mapOverlay = document.querySelector('[id^="shopify-section-"][id*="__map_section"] .map-section') || document.querySelector('.map-section');
    if (mapOverlay) {
      mapOverlay.classList.remove('visible');
      setTimeout(() => {
        mapOverlay.style.display = 'none';
        mapOverlay.style.pointerEvents = 'none';
      }, this.TRANSITION_DURATION);
    }

    // Show headers for quest
    this.updateHeaderVisibility(true);
    this.currentView = 'quest';

    if (window.toggleMerchantsDenBgVideo) window.toggleMerchantsDenBgVideo(false);
  }

  async showWorldMap() {
    if (this.currentView === 'world-map') {
      console.log('Already in world map, skipping');
      return;
    }

    console.log('Showing world map');
    await this.navigateTo('world-map');
  }

  // Internal method for actually displaying the world map (called by navigateTo)
  async _displayWorldMap() {
    console.log('StateManager: Displaying world map as full state');
    
    // Clear any pending timeouts for world map to prevent race conditions
    const mapTimeoutKey = 'world-map-hide';
    if (this.pendingTimeouts.has(mapTimeoutKey)) {
      console.log('StateManager: Clearing pending world map hide timeout');
      clearTimeout(this.pendingTimeouts.get(mapTimeoutKey));
      this.pendingTimeouts.delete(mapTimeoutKey);
    }
    
    // Hide all other sections first
    await this.hideUnnecessaryElements('world-map');
    
    // Show headers for world map (same as other states)
    this.updateHeaderVisibility(true);
    
    // Find the map section and show it as a full state
    const mapSection = document.querySelector('[id^="shopify-section-"][id*="__map_section"] .map-section') || 
                      document.querySelector('.map-section') ||
                      document.querySelector('.rpg-map-modal-section .map-section');
    
    if (mapSection) {
      console.log('StateManager: Found map section, displaying as full state');
      
      // Reset any previous state
      mapSection.style.transition = 'none';
      mapSection.style.display = 'block';
      mapSection.style.opacity = '0';
      mapSection.style.pointerEvents = 'none';
      
      // Force reflow
      mapSection.offsetHeight;
      
      // Enable transitions and show
      mapSection.style.transition = '';
      mapSection.style.opacity = '1';
      mapSection.classList.add('visible');
      mapSection.style.pointerEvents = 'auto';
      
      console.log('StateManager: World map displayed successfully as full state');
    } else {
      console.warn('StateManager: World map section not found. Check if rpg-map-modal section is properly loaded.');
      
      // Try to find any map-related sections for debugging
      const mapSections = document.querySelectorAll('.map-section, .rpg-map-modal-section');
      console.log('StateManager: Available map sections:', mapSections);
    }
  }


  async hideUnnecessaryElements(view) {
    console.log('🔍 hideUnnecessaryElements called for view:', view);
    console.log('   Current state before hiding:', {
      currentView: this.currentView,
      isMainMenuTransitioning: this.isMainMenuTransitioning,
      titleScreenDisplay: this.elements.titleScreen?.style.display,
      titleScreenOpacity: this.elements.titleScreen?.style.opacity,
      productSelectorDisplay: this.elements.productSelector?.style.display,
      productSelectorOpacity: this.elements.productSelector?.style.opacity
    });
    
    const hidePromises = [];
    
    // Always hide title screen unless we're showing it
    if (this.elements.titleScreen && view !== 'main-menu') {
      console.log('📱 Hiding title screen (view is not main-menu)');
      // Only hide if it's currently visible
      if (this.elements.titleScreen.style.display !== 'none' && this.elements.titleScreen.style.opacity !== '0') {
        console.log('   Title screen is visible, hiding it');
        this.elements.titleScreen.style.opacity = '0';
        this.elements.titleScreen.style.pointerEvents = 'none';
        hidePromises.push(new Promise(resolve => {
          setTimeout(() => {
            console.log('   Setting title screen display to none');
            this.elements.titleScreen.style.display = 'none';
            resolve();
          }, this.TRANSITION_DURATION);
        }));
      } else {
        console.log('   Title screen already hidden, skipping');
      }
    } else if (this.elements.titleScreen && view === 'main-menu') {
      console.log('📱 NOT hiding title screen (view is main-menu)');
      console.log('   Title screen current state:', {
        display: this.elements.titleScreen.style.display,
        opacity: this.elements.titleScreen.style.opacity,
        pointerEvents: this.elements.titleScreen.style.pointerEvents
      });
    } else if (!this.elements.titleScreen) {
      console.log('📱 Title screen element not found');
    }

    // Only hide product selector if we're not in merchant's den
    if (this.elements.productSelector && view !== 'merchants-den') {
      console.log('🛍️ Hiding product selector (view is not merchants-den)');
      // Only hide if it's currently visible
      if (this.elements.productSelector.style.display !== 'none' && this.elements.productSelector.style.opacity !== '0') {
        console.log('   Product selector is visible, hiding it');
        this.elements.productSelector.style.opacity = '0';
        this.elements.productSelector.style.pointerEvents = 'none';
        hidePromises.push(new Promise(resolve => {
          setTimeout(() => {
            console.log('   Setting product selector display to none');
            this.elements.productSelector.style.display = 'none';
            resolve();
          }, this.TRANSITION_DURATION);
        }));
      } else {
        console.log('   Product selector already hidden, skipping');
      }
    } else if (this.elements.productSelector && view === 'merchants-den') {
      console.log('🛍️ NOT hiding product selector (view is merchants-den)');
    } else if (!this.elements.productSelector) {
      console.log('🛍️ Product selector element not found');
    }

    // Hide guide section if we're not in guide view
    if (this.elements.guideSection && view !== 'guide') {
      console.log('📖 Hiding guide section (view is not guide)');
      this.elements.guideSection.classList.remove('visible');
      // Background video is now handled in the transition logic
      // if (window.toggleGuideBgVideo) window.toggleGuideBgVideo('none');
    } else if (this.elements.guideSection && view === 'guide') {
      console.log('📖 NOT hiding guide section (view is guide)');
    } else if (!this.elements.guideSection) {
      console.log('📖 Guide section element not found');
    }

    // Hide all RPG shops
    console.log('🏪 Hiding RPG shops (count:', this.elements.rpgShops.length, ')');
    this.elements.rpgShops.forEach((shop, index) => {
      // Only hide if it's currently visible
      if (shop.style.display !== 'none' && shop.style.opacity !== '0') {
        console.log(`   Hiding RPG shop ${index} (${shop.id || 'no-id'})`);
        shop.style.opacity = '0';
        hidePromises.push(new Promise(resolve => {
          setTimeout(() => {
            console.log(`   Setting RPG shop ${index} display to none`);
            shop.style.display = 'none';
            resolve();
          }, this.TRANSITION_DURATION);
        }));
      } else {
        console.log(`   RPG shop ${index} already hidden, skipping`);
      }
    });

    // Hide world map if we're not in world-map view
    if (view !== 'world-map') {
      console.log('🗺️ Hiding world map (view is not world-map)');
      const mapSection = document.querySelector('[id^="shopify-section-"][id*="__map_section"] .map-section') || 
                        document.querySelector('.map-section') ||
                        document.querySelector('.rpg-map-modal-section .map-section');
      
      if (mapSection && mapSection.style.display !== 'none' && mapSection.style.opacity !== '0') {
        console.log('   World map is visible, hiding it');
        mapSection.classList.remove('visible');
        mapSection.style.opacity = '0';
        mapSection.style.pointerEvents = 'none';
        
        // Clear any existing timeout for world map
        const mapTimeoutKey = 'world-map-hide';
        if (this.pendingTimeouts.has(mapTimeoutKey)) {
          clearTimeout(this.pendingTimeouts.get(mapTimeoutKey));
        }
        
        hidePromises.push(new Promise(resolve => {
          const timeoutId = setTimeout(() => {
            console.log('   Setting world map display to none');
            mapSection.style.display = 'none';
            this.pendingTimeouts.delete(mapTimeoutKey);
            resolve();
          }, this.TRANSITION_DURATION);
          
          this.pendingTimeouts.set(mapTimeoutKey, timeoutId);
        }));
      } else {
        console.log('   World map already hidden, skipping');
      }
    } else {
      console.log('🗺️ NOT hiding world map (view is world-map)');
    }
    
    // Wait for all hide operations to complete
    await Promise.all(hidePromises);
    console.log('✅ hideUnnecessaryElements completed for view:', view);
  }

  // Add a method to check if we can navigate
  canNavigate() {
    // If we're on main menu, only allow navigation to merchants-den, guide, or world-map
    if (this.currentView === 'main-menu') {
      return false; // This is handled by the navigateTo method's specific logic
    }
    return !this.isTransitioning;
  }

  // Helper method for timestamped logging
  logWithTimestamp(message, data = null) {
    const timestamp = Date.now();
    const timeSinceLastCall = timestamp - this.lastMainMenuCall;
    this.lastMainMenuCall = timestamp;
    
    const logMessage = `[${timestamp}] ${message}`;
    if (data) {
      console.log(logMessage, data, `(+${timeSinceLastCall}ms)`);
    } else {
      console.log(logMessage, `(+${timeSinceLastCall}ms)`);
    }
  }
}

// Initialize the state manager
const stateManager = new StateManager();

// Export for use in other files
window.stateManager = stateManager; 